import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useDevice } from '../contexts/DeviceContext';
import { useLoginPreferenceToggle } from '../hooks/useLoginPreferenceToggle';
import { formatDateISO } from '../lib/dateUtils';
import SessionForm from '../components/SessionForm';
import SessionList from '../components/SessionList';
import { SessionStatus } from '../lib/types';

interface Session {
  id: string;
  session_date: string;
  aircraft_type: string;
  flight_phase: string;
  status: SessionStatus;
  trainee: {
    full_name: string;
    email: string;
  };
  instructor: {
    full_name: string;
  };
  created_at: string;
}

export default function Sessions() {
  const { user } = useAuth();
  const { isMobile } = useDevice();
  const { prefersDashboard, handleToggle: handleToggleLoginPage, toastMessage } = useLoginPreferenceToggle();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState<string | undefined>();
  const userRole = user?.user_metadata?.role || 'trainee';

  console.log('[Sessions] User role:', userRole);
  console.log('[Sessions] User metadata:', user?.user_metadata);

  // Load sessions
  const loadSessions = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('sessions')
        .select(`
          id,
          session_date,
          aircraft_type,
          flight_phase,
          status,
          created_at,
          trainee:trainee_id(full_name, email),
          instructor:instructor_id(full_name)
        `)
        .order('session_date', { ascending: false });

      // Filter by role
      if (userRole === 'trainee') {
        query = query.eq('trainee_id', user?.id);
      } else if (userRole === 'instructor') {
        query = query.eq('instructor_id', user?.id);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Supabase error loading sessions:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        throw error;
      }

      console.log('Raw sessions data:', data);

      // Transform data to match expected format
      const transformedSessions = (data || []).map((session: any) => ({
        ...session,
        trainee: Array.isArray(session.trainee) ? session.trainee[0] : session.trainee,
        instructor: Array.isArray(session.instructor) ? session.instructor[0] : session.instructor,
      }));

      // Resolve org_pilot names for sessions where trainee FK is null
      const missingTraineeIds = transformedSessions
        .filter((s: any) => !s.trainee?.full_name)
        .map((s: any) => s.id);

      if (missingTraineeIds.length > 0) {
        const { data: crewData } = await supabase
          .from('session_crew')
          .select('session_id, crew_role, user:users(full_name), org_pilot:org_pilots(full_name)')
          .in('session_id', missingTraineeIds)
          .eq('crew_role', 'first_officer');

        if (crewData) {
          const crewMap = new Map<string, string>();
          for (const row of crewData as any[]) {
            const name = row.user?.full_name || row.org_pilot?.full_name;
            if (name) crewMap.set(row.session_id, name);
          }
          for (const session of transformedSessions) {
            if (!session.trainee?.full_name && crewMap.has(session.id)) {
              session.trainee = { full_name: crewMap.get(session.id)!, email: '' };
            }
          }
        }
      }

      setSessions(transformedSessions);
    } catch (error) {
      console.error('Error loading sessions:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, [user, userRole]);

  // Handle create new session
  const handleCreateNew = () => {
    setEditingSessionId(undefined);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle edit session
  const handleEdit = (sessionId: string) => {
    setEditingSessionId(sessionId);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle delete session
  const handleDelete = async (sessionId: string) => {
    const targetSession = sessions.find((session) => session.id === sessionId);
    if (targetSession?.status === 'published') {
      alert('Published sessions cannot be deleted.');
      return;
    }

    if (!confirm('Are you sure you want to delete this session? This will permanently remove all associated files, transcripts, assessments, and processing jobs.')) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token;

      if (!authToken) {
        throw new Error('Authentication required. Please sign in again.');
      }

      // Use dedicated delete-session API for complete cleanup
      const response = await fetch(`/api/delete-session?sessionId=${sessionId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        console.error('Delete error:', result);
        throw new Error(result.error || 'Failed to delete session');
      }

      console.log('Delete successful:', result);

      // Remove from local state
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));

      alert('Session deleted successfully');
    } catch (error: any) {
      console.error('Error deleting session:', error);
      alert(`Failed to delete session: ${error.message || 'Unknown error'}`);
    }
  };

  // Handle form success
  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingSessionId(undefined);
    loadSessions();
  };

  // Handle form cancel
  const handleFormCancel = () => {
    setShowForm(false);
    setEditingSessionId(undefined);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-steel/20 text-steel';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      default: return 'bg-steel/20 text-steel';
    }
  };

  // ─── Mobile View ──────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div className="space-y-4">
        {/* Mobile Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-navy">Sessions</h2>
          {userRole === 'instructor' && (
            <button
              onClick={handleCreateNew}
              className="min-w-[48px] min-h-[48px] flex items-center justify-center px-4 py-2 bg-navy text-white text-sm font-semibold rounded-xl"
            >
              + New
            </button>
          )}
        </div>

        {/* Session Form (mobile) */}
        {showForm && userRole === 'instructor' && (
          <div className="bg-white rounded-xl shadow-md p-4">
            <h3 className="text-base font-semibold text-navy mb-3">
              {editingSessionId ? 'Edit Session' : 'New Session'}
            </h3>
            <SessionForm
              sessionId={editingSessionId}
              onSuccess={handleFormSuccess}
              onCancel={handleFormCancel}
            />
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orca-elevate"></div>
            <p className="mt-2 text-steel text-sm">Loading sessions...</p>
          </div>
        )}

        {/* Mobile Session Cards */}
        {!loading && sessions.length > 0 && (
          <div className="space-y-3">
            {sessions.map((session) => (
              <div
                key={session.id}
                className="bg-white rounded-xl shadow-sm border border-steel/10 p-4"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-navy truncate">
                      {session.trainee?.full_name || 'Unknown Trainee'}
                    </p>
                    <p className="text-xs text-steel mt-0.5">
                      {formatDateISO(session.session_date)} · {session.aircraft_type}
                    </p>
                  </div>
                  <span
                    className={`ml-2 px-2 py-0.5 text-[10px] font-semibold rounded-full whitespace-nowrap ${getStatusColor(session.status)}`}
                  >
                    {session.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                {session.flight_phase && (
                  <p className="text-xs text-steel/70 mb-3">{session.flight_phase}</p>
                )}

                {/* Action buttons */}
                <div className="flex items-center gap-2 pt-2 border-t border-steel/10">
                  {session.status !== 'published' && (
                    <Link
                      to={`/session-workflow/${session.id}`}
                      className="flex-1 flex items-center justify-center gap-1.5 min-h-[44px] px-3 py-2 bg-navy text-white text-sm font-semibold rounded-lg active:bg-navy/80 transition-colors"
                    >
                      <span>🎙️</span> Record
                    </Link>
                  )}
                  <Link
                    to={`/sessions/${session.id}`}
                    className="flex-1 flex items-center justify-center min-h-[44px] px-3 py-2 border border-navy/20 text-navy text-sm font-semibold rounded-lg active:bg-navy/5 transition-colors"
                  >
                    View
                  </Link>
                  {session.status !== 'published' && userRole === 'instructor' && (
                    <button
                      onClick={() => handleEdit(session.id)}
                      className="flex items-center justify-center min-h-[44px] min-w-[44px] px-3 py-2 border border-steel/20 text-steel text-sm font-medium rounded-lg active:bg-steel/5 transition-colors"
                    >
                      Edit
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State (mobile) */}
        {!loading && sessions.length === 0 && !showForm && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="text-5xl mb-3">📝</div>
            <h3 className="text-lg font-semibold text-navy mb-1">No Sessions Yet</h3>
            <p className="text-sm text-steel mb-4">
              {userRole === 'instructor'
                ? 'Tap "+ New" to create your first session'
                : 'Your instructor will create sessions for you'}
            </p>
            {userRole === 'instructor' && (
              <button onClick={handleCreateNew} className="btn-primary text-sm px-6 py-3 min-h-[48px]">
                Create First Session
              </button>
            )}
          </div>
        )}

        {/* Login page toggle */}
        <div className="text-center pt-2 pb-4">
          <button
            onClick={handleToggleLoginPage}
            className="text-xs text-gray-400 hover:text-gray-300 transition-colors"
          >
            {prefersDashboard ? 'Show sessions on login' : 'Show dashboard on login'}
          </button>
        </div>

        {/* Toast */}
        {toastMessage && (
          <div className="fixed bottom-20 left-4 right-4 bg-navy text-white text-sm text-center py-3 px-4 rounded-xl shadow-lg z-50 animate-fade-in">
            {toastMessage}
          </div>
        )}
      </div>
    );
  }

  // ─── Desktop View (unchanged) ─────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-navy mb-2">Training Sessions</h2>
          <p className="text-steel">
            {userRole === 'instructor'
              ? 'Create and manage training sessions for your trainees'
              : 'View your training sessions and assessments'}
          </p>
        </div>

        {/* Create Button (Instructors only) */}
        {userRole === 'instructor' && !showForm && (
          <button onClick={handleCreateNew} className="btn-primary">
            + Create Session
          </button>
        )}
      </div>

      {/* Session Form */}
      {showForm && userRole === 'instructor' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-navy mb-4">
            {editingSessionId ? 'Edit Session' : 'Create New Session'}
          </h3>
          <SessionForm
            sessionId={editingSessionId}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        </div>
      )}

      {/* Sessions List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-navy mb-4">
          {userRole === 'instructor' ? 'Your Sessions' : 'My Training Sessions'}
        </h3>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orca-elevate"></div>
            <p className="mt-2 text-steel">Loading sessions...</p>
          </div>
        ) : (
          <SessionList
            sessions={sessions}
            onEdit={userRole === 'instructor' ? handleEdit : undefined}
            onDelete={userRole === 'instructor' ? handleDelete : undefined}
          />
        )}
      </div>

      {/* Empty State */}
      {!loading && sessions.length === 0 && !showForm && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <div className="text-6xl mb-4">📝</div>
          <h3 className="text-2xl font-semibold text-navy mb-2">No Sessions Yet</h3>
          <p className="text-steel mb-6">
            {userRole === 'instructor'
              ? 'Create your first training session to get started'
              : 'Your instructor will create training sessions for you'}
          </p>
          {userRole === 'instructor' && (
            <button onClick={handleCreateNew} className="btn-primary">
              Create Your First Session
            </button>
          )}
        </div>
      )}
    </div>
  );
}
